/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'
import styled from 'styled-components'
import Slider, { Settings as SliderSettings } from 'react-slick'
import 'slick-carousel/slick/slick.css'
import 'slick-carousel/slick/slick-theme.css'
import { RightOutlined } from '@ant-design/icons'
import colors from '@sportninja/common/constants/appColors'
import { isCanlan } from '@sportninja/common/utils/customer-name'
import { resolveAssetUrl } from '@sportninja/common/utils/assetUrl'
import useHomeDivisionStandings from '@sportninja/common/hooks/useHomeDivisionStandings'
import {
  getOrdinalSuffix,
  processStandingsData,
} from '@sportninja/common/ts-utils'
import { useEffect, useRef } from 'react'
import { Link } from 'react-router-dom'
import { ROUTES } from '@sportninja/common/constants/app'

const PrevArrow = (props: any) => {
  const { className, onClick, currentSlide } = props
  return (
    <div
      className={`${className} ${currentSlide === 0 ? 'slick-disabled' : ''}`}
      onClick={onClick}
      style={{ display: currentSlide === 0 ? 'none' : 'block' }}
    />
  )
}

const NextArrow = (props: any) => {
  const { className, onClick, slideCount, currentSlide } = props
  return (
    <div
      className={`${className} ${
        currentSlide === slideCount - 1 ? 'slick-disabled' : ''
      }`}
      onClick={onClick}
      style={{ display: currentSlide === slideCount - 1 ? 'none' : 'block' }}
    />
  )
}

/* Layout only — the card chrome (background, border, radius, padding) moved
   down onto SlideCard below.

   It used to live here, which put the bordered box around the SLIDER as a
   whole. That was invisible while the dots hung outside the flow on
   `position: absolute`, but once they were brought into flow to match the
   sibling widgets they landed inside the border. Scoping the chrome to the
   slide is how GameAttendanceCarousel and UpcomingEventsWidget are already
   built: each slide draws its own card, and the dot row is a plain sibling
   below it.

   The dot row carries its own 16px margin-top, so a flat 16px here puts the
   same 16px below it and the dots sit centred between this widget and the
   next. Was 32px, set when the dots were outside the flow. */
const CardWrapper = styled.div`
  width: 100%;
  color: white;
  margin-bottom: 16px;
`

/* The visible card. One per slide, so the dot row below sits OUTSIDE it. */
const SlideCard = styled.div`
  background-color: #212935;
  border-radius: 16px;
  border: 1px solid ${colors.SECONDARY_300};
  padding: 16px;
`

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  padding-bottom: 8px;
  border-bottom: 1px solid ${colors.SOFT_STEEL};
`

const Title = styled.h2`
  font-family: ${isCanlan
    ? 'Neue Plak Condensed, Arial, Helvetica, sans-serif;'
    : 'BarlowCondensed, Arial, Helvetica, sans-serif;'};
  font-size: 20px;
  font-weight: 600;
  line-height: 24px;
  margin: 0;
  color: ${colors.WHITE};
`

const SubTitle = styled.h3`
  font-family: ${isCanlan
    ? 'Neue Plak Condensed, Arial, Helvetica, sans-serif;'
    : 'BarlowCondensed, Arial, Helvetica, sans-serif;'};
  font-size: 16px;
  font-weight: 500;
  line-height: 20px;
  margin: 0;
  color: ${colors.NEUTRAL_50};
`

const ViewMore = styled.div`
  color: ${colors.PRIMARY};
  text-decoration: none;
  font-size: 14px;
  line-height: 17px;
  font-weight: 400;
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 8px;
`

const TeamInfo = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 16px;
  gap: 8px;
`

// ant design image component
const TeamLogo = styled.img`
  width: 32px !important;
  height: 32px !important;
  border-radius: 4px;
  background-color: white;
  box-shadow: 0px 4px 4px 0px rgba(0, 0, 0, 0.1);
  object-fit: contain;
`

const TeamName = styled.h3`
  font-family: ${isCanlan
    ? 'Neue Plak Condensed, Arial, Helvetica, sans-serif;'
    : 'BarlowCondensed, Arial, Helvetica, sans-serif;'};
  font-size: 20px;
  line-height: 24px;
  font-weight: 600;
  color: ${colors.WHITE};
  margin: 0;
`

const TeamPosition = styled.p`
  color: ${colors.NEUTRAL_50};
  font-size: 12px;
  line-height: 17px;
  font-weight: 400;
  margin: 0;
`

const StatsTable = styled.table`
  width: 100%;
  border-collapse: collapse;
`

const StatsHeader = styled.th`
  width: 32px;
  font-size: 14px;
  line-height: 20px;
  color: ${colors.NEUTRAL_50};
  font-weight: 500;
  padding-bottom: 8px;
  text-align: center;
`

const StatsData = styled.td`
  color: ${colors.WHITE};
  font-size: 14px;
  font-weight: bold;
  padding-bottom: 8px;
  font-size: 14px;
  line-height: 20px;
  font-weight: 400;
  text-align: center;
  width: 32px;
`

const CustomDot = styled.div`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: #7f8c8d;

  .slick-active & {
    background-color: ${colors.PRIMARY};
  }
`

const StyledSlider = styled(Slider)`
  /* The dot row is taken OUT of slick's absolute positioning and put back in
     normal flow, matching GameAttendanceCarousel and UpcomingEventsWidget so
     all three widgets in the sidebar space their dots identically.

     As shipped it is position:absolute / bottom:-36px, which floats the row in
     the gap below the card rather than occupying it — the gap above the dots
     is then set by that offset and the gap below by the card's own
     margin-bottom, so the two can never be matched. In flow, its margin-top
     sets the gap above and the wrapper's margin-bottom sets the gap below. */
  .slick-dots {
    position: static;
    display: flex !important;
    justify-content: center;
    align-items: center;
    margin-top: 16px;
    gap: 8px;
    li {
      margin: 0 0;
      width: 8px !important;
      height: 8px !important;
      /* The shipped button is 20x20 with 5px padding, which would set the
         row's height regardless of the 8px li. Matching it to the li keeps the
         row exactly as tall as the dots it shows. */
      button {
        width: 8px !important;
        height: 8px !important;
        padding: 0;
      }
    }
  }
  /* remove the margin of the arrows */
  /* slick centres its arrows on top:50% of the WHOLE slider. Now that the dot
     row is in normal flow, the slider is taller than the slide itself, so that
     50% lands below the card's centre. Offsetting by half the dot block (its
     16px margin-top + 8px dots = 24, so 12px) pulls them back onto the card's
     own vertical centre — the same correction the other two carousels make. */
  .slick-arrow {
    /* 16px inset, matching GameAttendanceCarousel and UpcomingEventsWidget —
       at zero the arrow sits hard against the slide's edge and is clipped by
       the card now that SlideCard draws the box. */
    margin: 0 16px;
    /* Above SlideCard: the card is an opaque, painted box drawn inside the
       slide, so at the slider's default stacking the arrow disappears behind
       it. The other two carousels get away with z-index 1 because their slides
       have no card chrome of their own. */
    z-index: 2;
    transform: translateY(calc(-50% - 12px));
  }

  /* the slides */
  .slick-slide {
    padding: 0 8px;
  }
  .slick-list {
    margin: 0 -8px;
  }
`

const CarouselContainer = styled.div`
  max-width: 400px;
  margin: 0 auto;
`

const settings: SliderSettings = {
  dots: true,
  infinite: false,
  speed: 500,
  slidesToShow: 1,
  slidesToScroll: 1,
  arrows: true,
  customPaging: (_i: number) => <CustomDot />,
  className: 'slick-carousel',
  prevArrow: <PrevArrow />,
  nextArrow: <NextArrow />,
}

const ViewMoreText = styled.p`
  color: ${colors.PRIMARY};
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: 17.4px;
  width: 100%;
`

interface DivisionStandingsCarouselProps {
  standingsData?: ReturnType<typeof useHomeDivisionStandings>['standingsData']
  standingsLoading?: boolean
}

const DivisionStandingsCarousel: React.FC<DivisionStandingsCarouselProps> = ({
  standingsData: propStandingsData,
  standingsLoading: propStandingsLoading,
}) => {
  const shouldFetchData = propStandingsData === undefined
  const {
    standingsData: hookStandingsData,
    standingsLoading: hookStandingsLoading,
    readStandings,
  } = useHomeDivisionStandings()
  const hasFetchedData = useRef(false)

  const standingsData = propStandingsData ?? hookStandingsData
  const standingsLoading = propStandingsLoading ?? hookStandingsLoading

  const teams = processStandingsData(standingsData)

  useEffect(() => {
    if (shouldFetchData && !hasFetchedData.current) {
      readStandings()
      hasFetchedData.current = true
    }
  }, [shouldFetchData, readStandings])

  if (standingsLoading || !standingsData || standingsData?.length === 0) {
    return null
  }

  if (teams?.length === 0) {
    return null
  }

  return (
    <CardWrapper>
      <CarouselContainer>
        <StyledSlider {...settings}>
          {teams.map((team, index) => (
            <div key={index}>
              <SlideCard>
                <Header>
                  <div style={{ maxWidth: '180px' }}>
                    <Title>{team.schedule_name}</Title>
                    <SubTitle>{team.schedule_root_name}</SubTitle>
                  </div>
                  <Link
                    to={`${ROUTES.SCHEDULE_ROOT}/${team.schedule_root_id}/standings?sn_division=${team.schedule_id}`}
                    css={css`
                      cursor: pointer;
                    `}
                  >
                    <ViewMore>
                      <ViewMoreText>View more</ViewMoreText>
                      <RightOutlined style={{ fontSize: '12px' }} />
                    </ViewMore>
                  </Link>
                </Header>
                <Link
                  to={`${ROUTES.TEAM_ROOT}/${team.team_id}`}
                  css={css`
                    cursor: pointer;
                  `}
                >
                  <TeamInfo>
                    <TeamLogo
                      src={
                        resolveAssetUrl(team.team_image) ||
                        '/images/generic_placeholders/placeholder_team.png'
                      }
                      alt={`${team.team_name} logo`}
                      onError={(e) => {
                        e.currentTarget.src =
                          '/images/generic_placeholders/placeholder_team.png'
                      }}
                    />
                    <div>
                      <TeamName>{team.team_name}</TeamName>
                      <TeamPosition>
                        Position {team.rank}
                        {getOrdinalSuffix(team.rank)}
                      </TeamPosition>
                    </div>
                  </TeamInfo>
                </Link>
                <StatsTable>
                  <thead>
                    <tr
                      style={{
                        display: 'flex',
                        justifyContent: 'space-around',
                        width: '100%',
                      }}
                    >
                      <StatsHeader>GP</StatsHeader>
                      <StatsHeader>W</StatsHeader>
                      <StatsHeader>L</StatsHeader>
                      <StatsHeader>T</StatsHeader>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      style={{
                        display: 'flex',
                        justifyContent: 'space-around',
                        width: '100%',
                      }}
                    >
                      <StatsData>{team.GP}</StatsData>
                      <StatsData>{team.W}</StatsData>
                      <StatsData>{team.L}</StatsData>
                      <StatsData>{team.T}</StatsData>
                    </tr>
                  </tbody>
                </StatsTable>
              </SlideCard>
            </div>
          ))}
        </StyledSlider>
      </CarouselContainer>
    </CardWrapper>
  )
}

export default DivisionStandingsCarousel
